import React, { useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export interface RadarChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    pointBackgroundColor: string;
    pointBorderColor: string;
    pointHoverBackgroundColor: string;
    pointHoverBorderColor: string;
  }>;
}

interface DetailedResult {
  question_text: string;
  answer_text: string;
  answer_score: number;
  category: string;
}

interface LandingPageRadarChartProps {
  data: RadarChartData;
  title?: string;
  detailedResults?: DetailedResult[];
  priorYearDetailedResults?: DetailedResult[];
}

/** Wrap long category labels so the radar plot can use more of the canvas. */
const wrapCategoryLabel = (label: string, maxChars: number): string | string[] => {
  if (!label || label.length <= maxChars) return label;
  const words = label.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxChars && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines.length > 1 ? lines : label;
};

const LandingPageRadarChart: React.FC<LandingPageRadarChartProps> = ({ data, title, detailedResults, priorYearDetailedResults }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Triggers below 900px (md breakpoint)

  // Outline-only series — fills obscure overlapping trend lines
  const chartData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: 'transparent',
      fill: false,
    })),
  };

  // Handle clicks on data points and category labels
  const handleChartClick = (event: any, elements: any[], chart: any) => {
    if (elements && elements.length > 0) {
      const element = elements[0];
      const index = element.index;
      const categoryLabel = data.labels[index];
      setSelectedCategory(categoryLabel);
      return;
    }

    const scale = chart.scales.r;
    if (!scale || !scale._pointLabelItems) return;

    const rect = chart.canvas.getBoundingClientRect();
    const x = event.native.clientX - rect.left;
    const y = event.native.clientY - rect.top;
    const ctx = chart.ctx;
    const pointLabelItems = scale._pointLabelItems;

    for (let i = 0; i < pointLabelItems.length; i++) {
      const item = pointLabelItems[i];
      const label = data.labels[i];
      
      const fontSize = item.font?.size || 12;
      const fontFamily = item.font?.family || 'Arial';
      ctx.font = `${fontSize}px ${fontFamily}`;
      const textMetrics = ctx.measureText(label);
      const labelWidth = textMetrics.width;
      const labelHeight = fontSize;
      
      let left, right, top, bottom;
      
      if (item.textAlign === 'right') {
        right = item.x;
        left = item.x - labelWidth;
      } else if (item.textAlign === 'left') {
        left = item.x;
        right = item.x + labelWidth;
      } else {
        left = item.x - labelWidth / 2;
        right = item.x + labelWidth / 2;
      }
      
      top = item.y - labelHeight;
      bottom = item.y + labelHeight / 2;

      const padding = 10;
      left -= padding;
      right += padding;
      top -= padding;
      bottom += padding;

      if (x >= left && x <= right && y >= top && y <= bottom) {
        setSelectedCategory(label);
        return;
      }
    }
  };

  // Get questions for selected category
  const selectedCategoryQuestions = selectedCategory && detailedResults
    ? detailedResults.filter(result => {
        const categoryParts = result.category.split(' - ');
        const level02 = categoryParts.length > 1 ? categoryParts[1] : result.category;
        return level02 === selectedCategory;
      })
    : [];

  // Get scores for selected category (current and prior year)
  const selectedCategoryIndex = selectedCategory ? data.labels.indexOf(selectedCategory) : -1;
  const selectedCategoryScore = selectedCategoryIndex >= 0 && data.datasets[0]
    ? data.datasets[0].data[selectedCategoryIndex] || 0
    : 0;
  const selectedCategoryPriorScore = selectedCategoryIndex >= 0 && data.datasets[1]
    ? data.datasets[1].data[selectedCategoryIndex] || 0
    : 0;

  // Plugin to handle cursor changes
  const cursorPlugin = {
    id: 'cursorPlugin',
    afterEvent: (chart: any, args: any) => {
      const event = args.event;
      if (event.type !== 'mousemove') return;

      const scale = chart.scales.r;
      if (!scale || !scale._pointLabelItems) return;

      const rect = chart.canvas.getBoundingClientRect();
      const x = event.native.clientX - rect.left;
      const y = event.native.clientY - rect.top;

      let overLabel = false;
      const ctx = chart.ctx;
      const pointLabelItems = scale._pointLabelItems;

      for (let i = 0; i < pointLabelItems.length; i++) {
        const item = pointLabelItems[i];
        const label = chart.data.labels[i];
        
        const fontSize = item.font?.size || 12;
        const fontFamily = item.font?.family || 'Arial';
        ctx.font = `${fontSize}px ${fontFamily}`;
        const textMetrics = ctx.measureText(label);
        const labelWidth = textMetrics.width;
        const labelHeight = fontSize;
        
        let left, right, top, bottom;
        
        if (item.textAlign === 'right') {
          right = item.x;
          left = item.x - labelWidth;
        } else if (item.textAlign === 'left') {
          left = item.x;
          right = item.x + labelWidth;
        } else {
          left = item.x - labelWidth / 2;
          right = item.x + labelWidth / 2;
        }
        
        top = item.y - labelHeight;
        bottom = item.y + labelHeight / 2;

        const padding = 10;
        left -= padding;
        right += padding;
        top -= padding;
        bottom += padding;

        if (x >= left && x <= right && y >= top && y <= bottom) {
          overLabel = true;
          break;
        }
      }

      chart.canvas.style.cursor = overLabel ? 'pointer' : 'default';
    }
  };

  // Custom plugin to draw percentage labels below category labels
  const percentageLabelsPlugin = {
    id: 'percentageLabels',
    afterDraw: (chart: any) => {
      const ctx = chart.ctx;
      const scale = chart.scales.r;
      
      if (!scale || !scale._pointLabelItems) return;
      
      const pointLabelItems = scale._pointLabelItems;
      
      // Draw percentages for each dataset below the category labels
      pointLabelItems.forEach((item: any, index: number) => {
        const x = item.x;
        const y = item.y;
        const label = data.labels[index];
        const wrapped = wrapCategoryLabel(label, isMobile ? 14 : 18);
        const labelLines = Array.isArray(wrapped) ? wrapped.length : 1;
        
        // Calculate vertical offset based on label alignment, mobile/desktop, and wrapped lines
        const labelHeight = isMobile ? 12 : 15; // Approximate height per line
        const baseOffset = labelLines * labelHeight;
        
        // Adjust based on position of the label relative to chart center
        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
        
        let yOffset;
        let isTopLabel = false;
        
        // Check if this is a top label (above center and near horizontal center)
        if (y < centerY && Math.abs(x - centerX) < 100) {
          // Top center label - position scores ABOVE the label text (negative offset)
          isTopLabel = true; // Always use top positioning for top center label
          yOffset = isMobile ? -18 : -20; // Negative offset to go above label
        } else if (y < centerY - 20) {
          // Other top labels - position below with consistent spacing
          yOffset = baseOffset + (isMobile ? 18 : 8);
        } else if (y > centerY + 50) {
          // Bottom labels - consistent spacing
          yOffset = baseOffset + (isMobile ? 10 : 6);
        } else {
          // Side labels (left/right) - consistent tight spacing
          yOffset = baseOffset + (isMobile ? 6 : 4);
        }
        
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = isTopLabel ? 'bottom' : 'top'; // Change baseline for top label
        
        // Draw each dataset's percentage
        data.datasets.forEach((dataset, datasetIndex) => {
          const value = dataset.data[index];
          const text = `${Math.round(value)}%`;
          
          ctx.font = isMobile ? 'bold 9px Arial' : 'bold 11px Arial'; // Smaller font to reduce overlap
          ctx.fillStyle = dataset.borderColor;
          
          // Stack percentages vertically if multiple datasets
          const lineHeight = isMobile ? 12 : 14;
          const yPosition = y + yOffset + (datasetIndex * lineHeight);
          
          // Add shadow for better visibility
          ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          ctx.fillText(text, x, yPosition);
        });
        
        ctx.restore();
      });
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: '#FFFFFF',
          font: {
            size: isMobile ? 11 : 13, // Smaller font on mobile
            weight: 'normal' as const,
          },
          padding: 4,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: false, // Disable tooltips completely
      },
      datalabels: {
        display: false, // Disable any datalabels plugin if present
      },
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: false,
    },
    hover: {
      mode: null as any, // Disable hover mode to prevent any overlays
    },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(255, 255, 255, 0.1)',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        min: 0,
        max: 100,
        ticks: {
          display: false, // Hide the 20%, 40%, 60%, 80%, 100% labels
          stepSize: 20,
        },
        pointLabels: {
          font: {
            size: isMobile ? 10 : 12,
            weight: 'bold' as const,
          },
          color: '#1976d2', // Blue color to indicate clickability
          padding: isMobile ? 3 : 6,
          // Wrap labels on all viewports so the radar plot can grow
          callback: function(label: string) {
            return wrapCategoryLabel(label, isMobile ? 14 : 18);
          },
        },
      },
    },
    elements: {
      point: {
        radius: isMobile ? 4 : 5,
        hoverRadius: isMobile ? 6 : 7,
        borderWidth: 2,
      },
      line: {
        borderWidth: 2.5,
      },
    },
    layout: {
      padding: {
        top: 8,
        bottom: isMobile ? 24 : 28,
        left: isMobile ? 5 : 8,
        right: isMobile ? 5 : 8,
      },
    },
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, backgroundColor: '#000000', color: '#FFFFFF' }}>
      {title && (
        <Typography variant="h6" align="center" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, // Stack vertically on mobile, horizontal on desktop
        gap: 2, 
        minHeight: { xs: 'auto', md: '560px' },
      }}>
        {/* Radar Chart */}
        <Box sx={{ 
          flex: { xs: '1', md: '0 0 65%' }, 
          minHeight: { xs: '450px', md: '560px' },
          height: { xs: '450px', md: 'auto' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Radar data={chartData as any} options={options} plugins={[cursorPlugin, percentageLabelsPlugin]} />
        </Box>

        {/* Questions Detail Panel */}
        <Box sx={{ 
          flex: '1', 
          borderLeft: { xs: 0, md: 1 }, // Remove left border on mobile
          borderTop: { xs: 2, md: 0 }, // Add top border on mobile
          borderColor: 'rgba(255, 255, 255, 0.1)', 
          pl: { xs: 0, md: 3 }, // Remove left padding on mobile
          pt: { xs: 3, md: 0 }, // Add top padding on mobile
        }}>
          {!selectedCategory ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <Typography variant="body1">
                Click on a category label or data point to view detailed questions and scores
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Category Header */}
              <Box sx={{ 
                mb: 3, 
                pb: 2, 
                borderBottom: '2px solid rgba(229, 57, 53, 0.3)',
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#FFFFFF',
                    mb: 1.5,
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {selectedCategory}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={`Score: ${Math.round(selectedCategoryScore)}%`}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      height: '36px',
                      backgroundColor: selectedCategoryScore >= 80 
                        ? 'rgba(76, 175, 80, 0.2)' 
                        : selectedCategoryScore >= 50 
                        ? 'rgba(255, 152, 0, 0.2)' 
                        : 'rgba(244, 67, 54, 0.2)',
                      color: selectedCategoryScore >= 80 
                        ? '#4caf50' 
                        : selectedCategoryScore >= 50 
                        ? '#ff9800' 
                        : '#f44336',
                      border: '2px solid',
                      borderColor: selectedCategoryScore >= 80 
                        ? '#4caf50' 
                        : selectedCategoryScore >= 50 
                        ? '#ff9800' 
                        : '#f44336',
                    }}
                  />
                  {data.datasets.length > 1 && selectedCategoryPriorScore > 0 && (
                    <Chip
                      icon={selectedCategoryScore >= selectedCategoryPriorScore ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      label={`${Math.abs(Math.round(selectedCategoryScore - selectedCategoryPriorScore))}% vs Last Year`}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        height: '36px',
                        backgroundColor: selectedCategoryScore >= selectedCategoryPriorScore 
                          ? 'rgba(76, 175, 80, 0.15)' 
                          : 'rgba(244, 67, 54, 0.15)',
                        color: selectedCategoryScore >= selectedCategoryPriorScore ? '#4caf50' : '#f44336',
                        '& .MuiChip-icon': {
                          color: selectedCategoryScore >= selectedCategoryPriorScore ? '#4caf50' : '#f44336',
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Questions List */}
              {selectedCategoryQuestions.length > 0 ? (
                <Box sx={{ maxHeight: '420px', overflow: 'auto', pr: 1 }}>
                  {selectedCategoryQuestions.map((result, index) => {
                    // Find matching prior year question
                    const priorYearResult = priorYearDetailedResults?.find(pr => 
                      pr.question_text === result.question_text && 
                      pr.category === result.category
                    );
                    const priorScore = priorYearResult?.answer_score || 0;
                    const scoreDiff = result.answer_score - priorScore;
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2.5,
                          pb: 2.5,
                          borderBottom: index < selectedCategoryQuestions.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          pl: 2,
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '3px',
                            backgroundColor: result.answer_score >= 80 
                              ? '#4caf50' 
                              : result.answer_score >= 50 
                              ? '#ff9800' 
                              : '#f44336',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            transform: 'translateX(4px)',
                            '&:before': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {/* Question */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#FFFFFF',
                            fontWeight: 600,
                            mb: 0.75,
                            lineHeight: 1.5,
                            fontSize: '0.95rem',
                          }}
                        >
                          {result.question_text}
                        </Typography>
                        
                        {/* Answer */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.65)',
                            fontStyle: 'italic',
                            lineHeight: 1.5,
                            mb: 1,
                            fontSize: '0.9rem',
                          }}
                        >
                          {result.answer_text}
                        </Typography>
                        
                        {/* Score and Change - below answer */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={`${result.answer_score.toFixed(0)}%`}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.85rem',
                              backgroundColor: result.answer_score >= 80 
                                ? 'rgba(76, 175, 80, 0.2)' 
                                : result.answer_score >= 50 
                                ? 'rgba(255, 152, 0, 0.2)' 
                                : 'rgba(244, 67, 54, 0.2)',
                              color: result.answer_score >= 80 
                                ? '#4caf50' 
                                : result.answer_score >= 50 
                                ? '#ff9800' 
                                : '#f44336',
                              border: '1px solid',
                              borderColor: result.answer_score >= 80 
                                ? '#4caf50' 
                                : result.answer_score >= 50 
                                ? '#ff9800' 
                                : '#f44336',
                            }}
                          />
                          
                          {priorYearResult && scoreDiff !== 0 && (
                            <Chip
                              icon={scoreDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                              label={`${Math.abs(scoreDiff).toFixed(0)}%`}
                              size="small"
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: scoreDiff > 0 
                                  ? 'rgba(76, 175, 80, 0.1)' 
                                  : 'rgba(244, 67, 54, 0.1)',
                                color: scoreDiff > 0 ? '#4caf50' : '#f44336',
                                '& .MuiChip-icon': {
                                  color: scoreDiff > 0 ? '#4caf50' : '#f44336',
                                  fontSize: '16px',
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No detailed questions available for this category.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default LandingPageRadarChart;

